import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  gruposAPI,
  formasPagoAPI,
  bancosAPI,
  cuentasBancariasAPI,
  cajasAPI,
  movimientosPredefinidosAPI,
  recibosCobroGrupoAPI,
} from '../services/api';
import { toast } from 'react-toastify';
import { useZonaHoraria } from '../context/ZonaHorariaContext';
import { useBranding } from '../context/BrandingContext';
import { imprimirReciboGrupoDual } from '../utils/comprobantePrint';
import FormasPagoEditor from '../components/FormasPagoEditor';
import { camposDolar } from '../utils/montoDolarFormaPago';
import '../Clientes.css';

const formatMoneda = (n) =>
  parseFloat(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const esChequeTipoPropio = (nombre) => {
  const n = (nombre || '').toLowerCase();
  return n.includes('cheque propio') || n.includes('cheque de tercero');
};

const esFpBancariaSinCaja = (nombre) => {
  const n = (nombre || '').toLowerCase();
  return n.includes('transferencia') || esChequeTipoPropio(n);
};

const toCents = (v) => Math.round((Number(v) || 0) * 100);
const fromCents = (c) => Math.round(c) / 100;

/** Reparto por orden con tope en el saldo; remanente al primer cliente (espejo del backend). */
function repartoOrden(items, monto) {
  if (!items.length) return new Map();
  const montoCents = toCents(monto);
  let restante = montoCents;
  const asignados = items.map((it) => {
    const saldoCents = Math.max(toCents(it.saldo), 0);
    const asignar = Math.max(Math.min(restante, saldoCents), 0);
    restante -= asignar;
    return { cliente_id: it.cliente_id, cents: asignar };
  });
  if (restante > 0) asignados[0].cents += restante;
  return new Map(asignados.map((a) => [a.cliente_id, fromCents(a.cents)]));
}

const ReciboGrupoForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { fechaHoy, formatFecha } = useZonaHoraria();
  const { logoUrl } = useBranding();

  const [grupos, setGrupos] = useState([]);
  const [grupoId, setGrupoId] = useState('');
  const [clientesGrupo, setClientesGrupo] = useState([]);
  const [config, setConfig] = useState({}); // cliente_id -> { selected, monto, orden }
  const [modo, setModo] = useState('manual');
  const [formData, setFormData] = useState({ monto: '', fecha: '', concepto: '' });
  const [formasPagoRecibo, setFormasPagoRecibo] = useState([]);
  const [formasPago, setFormasPago] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [cuentasBancarias, setCuentasBancarias] = useState([]);
  const [cajas, setCajas] = useState([]);
  const [predefinidoId, setPredefinidoId] = useState(location.state?.movimientoPredefinidoId || null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setFormData((prev) => ({ ...prev, fecha: prev.fecha || fechaHoy() }));
  }, [fechaHoy]);

  const loadCatalogos = useCallback(async () => {
    try {
      const [resGrupos, resFp, resBancos, resCb, resCajas, resPre] = await Promise.all([
        gruposAPI.getAll(),
        formasPagoAPI.getDisponibles({ tipo_movimiento: 'ingreso' }),
        bancosAPI.getAll({ activo: true }),
        cuentasBancariasAPI.getAll({ activo: true }),
        cajasAPI.getAll({ activo: true }),
        movimientosPredefinidosAPI.getAll({ activo: true, uso: 'clientes' }),
      ]);
      if (resGrupos.success) setGrupos(resGrupos.data || []);
      if (resFp.success) setFormasPago(resFp.data || []);
      if (resBancos.success) setBancos(resBancos.data || []);
      if (resCb.success) setCuentasBancarias(resCb.data || []);
      if (resCajas.success) setCajas(resCajas.data || []);
      if (!predefinidoId && resPre.success) {
        const pre = (resPre.data || []).find(
          (m) => (m.descripcion || '').trim().toLowerCase() === 'recibo de cobro grupo'
        );
        if (pre) setPredefinidoId(pre.id);
      }
    } catch (e) {
      console.error('Error al cargar catálogos:', e);
      toast.error('No se pudieron cargar los datos iniciales');
    }
  }, [predefinidoId]);

  useEffect(() => {
    loadCatalogos();
  }, [loadCatalogos]);

  const loadClientesGrupo = useCallback(async (id) => {
    if (!id) {
      setClientesGrupo([]);
      setConfig({});
      return;
    }
    try {
      const res = await gruposAPI.getClientes(id);
      if (res.success) {
        const lista = res.data || [];
        setClientesGrupo(lista);
        const nuevoConfig = {};
        lista.forEach((c, idx) => {
          nuevoConfig[c.id] = { selected: true, monto: '', orden: idx + 1 };
        });
        setConfig(nuevoConfig);
      }
    } catch (e) {
      console.error('Error al cargar clientes del grupo:', e);
      toast.error('No se pudieron cargar los clientes del grupo');
    }
  }, []);

  const handleGrupoChange = (e) => {
    const id = e.target.value;
    setGrupoId(id);
    loadClientesGrupo(id);
  };

  const setConfigCampo = (clienteId, campo, valor) => {
    setConfig((prev) => ({ ...prev, [clienteId]: { ...prev[clienteId], [campo]: valor } }));
  };

  const seleccionarTodos = (selected) => {
    setConfig((prev) => {
      const next = { ...prev };
      clientesGrupo.forEach((c) => { next[c.id] = { ...next[c.id], selected }; });
      return next;
    });
  };

  const seleccionados = useMemo(
    () => clientesGrupo.filter((c) => config[c.id]?.selected),
    [clientesGrupo, config]
  );

  const reparto = useMemo(() => {
    if (modo === 'manual') {
      return new Map(seleccionados.map((c) => [c.id, parseFloat(config[c.id]?.monto) || 0]));
    }
    const monto = parseFloat(formData.monto || 0);
    if (!seleccionados.length || isNaN(monto) || monto <= 0) return new Map();
    const items = seleccionados
      .map((c) => ({ cliente_id: c.id, saldo: parseFloat(c.saldo) || 0, orden: Number(config[c.id]?.orden) || 0 }))
      .sort((a, b) => a.orden - b.orden);
    return repartoOrden(items, monto);
  }, [seleccionados, config, modo, formData.monto]);

  const totalAsignado = useMemo(
    () => Array.from(reparto.values()).reduce((a, b) => a + b, 0),
    [reparto]
  );

  const construirFormasPagoPayload = () =>
    formasPagoRecibo.map((fp) => {
      const formaPagoObj = formasPago.find((f) => String(f.id) === String(fp.forma_pago_id));
      const nombre = formaPagoObj?.nombre?.toLowerCase() || '';
      const resultado = {
        forma_pago_id: fp.forma_pago_id,
        forma_pago_nombre: formaPagoObj?.nombre,
        monto: parseFloat(fp.monto),
        ...camposDolar(fp, { formasPago, cuentasBancarias, cajas, modoEgreso: false }),
      };
      if (esChequeTipoPropio(nombre)) {
        const esTercero = nombre.includes('cheque de tercero');
        resultado.cheque_propio = {
          cuenta_bancaria_id: esTercero ? null : (fp.cheque_propio?.cuenta_bancaria_id || null),
          banco_id: esTercero ? (fp.cheque_propio?.banco_id || null) : null,
          numero: fp.cheque_propio?.numero,
          titular: esTercero ? (fp.cheque_propio?.titular || null) : null,
          fecha_emision: fp.cheque_propio?.fecha_emision || fechaHoy(),
          fecha_cobro: fp.cheque_propio?.fecha_cobro || null,
          importe: parseFloat(fp.monto),
          es_electronico: Boolean(fp.cheque_propio?.es_electronico),
        };
      } else if (nombre.includes('cheque')) {
        resultado.cheque = {
          banco_id: fp.cheque?.banco_id,
          numero_cheque: fp.cheque?.numero_cheque,
          importe: parseFloat(fp.monto),
          fecha_emision: fp.cheque?.fecha_emision,
          fecha_cobro: fp.cheque?.fecha_cobro || null,
          titular: fp.cheque?.titular,
        };
      } else if (nombre.includes('transferencia')) {
        const tr = fp.transferencia || {};
        resultado.transferencia = {
          cuenta_bancaria_id: tr.cuenta_bancaria_id ?? formaPagoObj?.cuenta_bancaria_id ?? null,
          fecha: tr.fecha || formData.fecha,
          monto: parseFloat(tr.monto != null && tr.monto !== '' ? tr.monto : fp.monto),
        };
      } else if (nombre.includes('tarjeta')) {
        resultado.tarjeta = { numero_ticket: fp.tarjeta?.numero_ticket };
        if (fp.caja_id) resultado.caja_id = Number(fp.caja_id);
      } else if (fp.caja_id) {
        resultado.caja_id = Number(fp.caja_id);
      }
      return resultado;
    });

  const formasPagoHabilitadas = useMemo(
    () =>
      formasPago.filter((f) => {
        const tm = (f.tipo_movimiento || '').toLowerCase();
        const nombre = (f.nombre || '').toLowerCase();
        return (tm === 'ingreso' || tm === 'ambos') && !nombre.includes('cheque propio');
      }),
    [formasPago]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    const monto = parseFloat(formData.monto || 0);
    if (!grupoId) return toast.error('Seleccione un grupo');
    if (isNaN(monto) || monto <= 0) return toast.error('Ingrese un monto mayor a 0');
    if (!formData.fecha) return toast.error('Ingrese la fecha');
    if (!formData.concepto.trim()) return toast.error('Ingrese el concepto');
    if (seleccionados.length === 0) return toast.error('Seleccione al menos un cliente del grupo');
    if (formasPagoRecibo.length === 0) return toast.error('Agregue al menos una forma de pago');
    const totalFp = formasPagoRecibo.reduce((s, fp) => s + parseFloat(fp.monto || 0), 0);
    if (Math.abs(totalFp - monto) > 0.01) {
      return toast.error(`La suma de las formas de pago (${formatMoneda(totalFp)}) debe ser igual al monto (${formatMoneda(monto)})`);
    }
    for (const fp of formasPagoRecibo) {
      const formaPagoObj = formasPago.find((f) => String(f.id) === String(fp.forma_pago_id));
      const nombre = (formaPagoObj?.nombre || '').toLowerCase();
      if (formaPagoObj?.requiere_seleccion_fondo && !esFpBancariaSinCaja(nombre) && !fp.caja_id) {
        return toast.error(`Seleccione la caja para ${formaPagoObj.nombre || 'la forma de pago'}`);
      }
      if (nombre.includes('transferencia')) {
        const cuenta = fp.transferencia?.cuenta_bancaria_id || formaPagoObj?.cuenta_bancaria_id;
        if (!cuenta) return toast.error('Transferencia: seleccione la cuenta bancaria');
      }
      if (nombre.includes('cheque de tercero')) {
        if (!fp.cheque_propio?.banco_id || !String(fp.cheque_propio?.numero || '').trim() || !fp.cheque_propio?.fecha_emision) {
          return toast.error('Cheque de tercero: complete banco, número y fecha de emisión');
        }
      }
      if (nombre.includes('tarjeta') && !String(fp.tarjeta?.numero_ticket || '').trim()) {
        return toast.error('Tarjeta: indique el número de ticket');
      }
    }
    if (modo === 'manual') {
      const totalAsig = seleccionados.reduce((s, c) => s + (parseFloat(config[c.id]?.monto) || 0), 0);
      if (Math.abs(totalAsig - monto) > 0.01) {
        return toast.error(`La suma de los montos asignados (${formatMoneda(totalAsig)}) debe ser igual al monto del recibo (${formatMoneda(monto)})`);
      }
    }

    const clientesPayload = seleccionados.map((c) => ({
      cliente_id: c.id,
      monto: modo === 'manual' ? (parseFloat(config[c.id]?.monto) || 0) : null,
      orden: modo === 'orden' ? (Number(config[c.id]?.orden) || 0) : null,
    }));

    setLoading(true);
    try {
      const res = await recibosCobroGrupoAPI.crear({
        grupo_id: Number(grupoId),
        monto,
        fecha: formData.fecha,
        concepto: formData.concepto,
        modo_reparto: modo,
        movimiento_predefinido_id: predefinidoId,
        clientes: clientesPayload,
        formas_pago: construirFormasPagoPayload(),
      });
      if (res.success) {
        const numeroRecibo = res.data?.numero_recibo;
        const reciboId = res.data?.id;
        toast.success(
          ({ closeToast }) => (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span>Recibo de grupo N° {numeroRecibo} creado</span>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="btn-primary"
                  style={{ padding: '4px 10px', fontSize: 13 }}
                  onClick={async () => {
                    closeToast();
                    try {
                      const det = await recibosCobroGrupoAPI.getById(reciboId);
                      if (det.success && det.data) {
                        imprimirReciboGrupoDual(det.data, formatFecha, logoUrl);
                      } else {
                        toast.error('No se pudo cargar el recibo para imprimir');
                      }
                    } catch (e) {
                      toast.error(e.message || 'Error al imprimir');
                    }
                  }}
                >
                  Imprimir
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  style={{ padding: '4px 10px', fontSize: 13 }}
                  onClick={() => {
                    closeToast();
                    navigate('/movimientos');
                  }}
                >
                  Ir a movimientos
                </button>
              </div>
            </div>
          ),
          { autoClose: 12000 }
        );
      } else {
        toast.error(res.message || 'No se pudo crear el recibo de grupo');
      }
    } catch (err) {
      toast.error(err.message || 'No se pudo crear el recibo de grupo');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="clientes-container">
      <div className="clientes-header">
        <h1>Nuevo Recibo de Cobro por Grupo</h1>
      </div>

      <form onSubmit={handleSubmit} className="cliente-form" style={{ maxWidth: '1000px' }}>
        <div className="form-row">
          <div className="form-group">
            <label>Grupo de clientes *</label>
            <select value={grupoId} onChange={handleGrupoChange} required>
              <option value="">Seleccione un grupo...</option>
              {grupos.map((g) => (
                <option key={g.id} value={g.id}>{g.nombre}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Fecha *</label>
            <input type="date" value={formData.fecha} onChange={(e) => setFormData((p) => ({ ...p, fecha: e.target.value }))} required />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Monto total del recibo *</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formData.monto}
              onChange={(e) => setFormData((p) => ({ ...p, monto: e.target.value }))}
              required
              placeholder="0.00"
            />
          </div>
          <div className="form-group">
            <label>Concepto *</label>
            <input type="text" value={formData.concepto} onChange={(e) => setFormData((p) => ({ ...p, concepto: e.target.value }))} required placeholder="Concepto del recibo" />
          </div>
        </div>

        <div className="form-group">
          <div style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: '16px' }}>
            <label style={{ margin: 0, whiteSpace: 'nowrap' }}>Modo de reparto *</label>
            <label className="checkbox-inline" style={{ whiteSpace: 'nowrap' }}>
              <input type="radio" name="modo" checked={modo === 'manual'} onChange={() => setModo('manual')} />
              <span>Asignación manual de montos</span>
            </label>
            <label className="checkbox-inline" style={{ whiteSpace: 'nowrap' }}>
              <input type="radio" name="modo" checked={modo === 'orden'} onChange={() => setModo('orden')} />
              <span>Por orden (cascada hasta el saldo)</span>
            </label>
          </div>
        </div>

        {clientesGrupo.length > 0 && (
          <div className="form-group">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <label style={{ margin: 0 }}>Clientes del grupo</label>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button type="button" className="btn-link-multiselect" onClick={() => seleccionarTodos(true)}>Seleccionar todos</button>
                <button type="button" className="btn-link-multiselect" onClick={() => seleccionarTodos(false)}>Deseleccionar todos</button>
              </div>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table className="data-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}></th>
                    <th>Cliente</th>
                    <th style={{ textAlign: 'right' }}>Saldo</th>
                    {modo === 'manual' ? <th style={{ width: '140px' }}>Monto a asignar</th> : <th style={{ width: '90px' }}>Orden</th>}
                    <th style={{ textAlign: 'right', width: '130px' }}>Asignado</th>
                  </tr>
                </thead>
                <tbody>
                  {clientesGrupo.map((c) => {
                    const cfg = config[c.id] || {};
                    return (
                      <tr key={c.id} style={{ opacity: cfg.selected ? 1 : 0.5 }}>
                        <td>
                          <input type="checkbox" checked={Boolean(cfg.selected)} onChange={(e) => setConfigCampo(c.id, 'selected', e.target.checked)} />
                        </td>
                        <td>{c.razon_social}</td>
                        <td style={{ textAlign: 'right' }}>${formatMoneda(c.saldo)}</td>
                        {modo === 'manual' ? (
                          <td>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={cfg.monto ?? ''}
                              disabled={!cfg.selected}
                              onChange={(e) => setConfigCampo(c.id, 'monto', e.target.value)}
                              placeholder="0.00"
                              style={{ width: '120px', padding: '4px 6px' }}
                            />
                          </td>
                        ) : (
                          <td>
                            <input
                              type="number"
                              step="1"
                              min="1"
                              value={cfg.orden ?? ''}
                              disabled={!cfg.selected}
                              onChange={(e) => setConfigCampo(c.id, 'orden', e.target.value)}
                              style={{ width: '70px', padding: '4px 6px' }}
                            />
                          </td>
                        )}
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>
                          {cfg.selected ? `$${formatMoneda(reparto.get(c.id) || 0)}` : '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ marginTop: '8px', textAlign: 'right' }}>
              <strong>Total asignado: ${formatMoneda(totalAsignado)}</strong>
              {' · '}
              <span>Monto: ${formatMoneda(formData.monto)}</span>
            </div>
          </div>
        )}

        <FormasPagoEditor
          value={formasPagoRecibo}
          onChange={setFormasPagoRecibo}
          formasPago={formasPagoHabilitadas}
          bancos={bancos}
          setBancos={setBancos}
          cuentasBancarias={cuentasBancarias}
          cajas={cajas}
          fecha={formData.fecha}
          fechaHoy={fechaHoy}
          montoTotal={formData.monto}
        />

        <div className="form-actions">
          <button type="button" className="btn-secondary" onClick={() => navigate('/movimientos')}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Guardando...' : 'Crear Recibo de Grupo'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ReciboGrupoForm;
